import { Container, Box, Flex, Pagination } from '@legion-ui/core';
import UsersTable from '@/components/UsersTable';
import PageHeader from '@/components/PageHeader';
import { useUserQuery } from '@/hooks/useUserQuery';
import { tableStore } from '@/store/table';
import { useSearchParams } from 'react-router-dom';
import { userStore } from '@/store/users';
import { ChangeEvent, useEffect, useState } from 'react';
import { setLocalStorage } from '@/services';
import { useDebounce } from '@/hooks/useDebounce';
import {
  checkIfUserAdmin,
  checkIfUserDeveloper,
  checkIfUserPublisher,
  getUserAccessByPortal,
  getUserByUsername,
} from '@/services/utilities';
import Cookies from 'js-cookie';

const UsersPage = () => {
  // FETCH DATA USING REACT QUERY
  const [searchParam, setSearchParam] = useSearchParams();
  const { userLogedIn } = userStore((state) => state);
  const { isFetching, refetch } = useUserQuery();
  

  const accessToken = Cookies.get('admin_portal_access_token') ?? '';
  const refreshToken = Cookies.get('admin_portal_refresh_token') ?? '';
  
  useEffect(() => {
    if (accessToken || refreshToken) {
      setLocalStorage('accessToken', accessToken);
      setLocalStorage('refreshToken', refreshToken);
    }
    //console.log('admin_portal_access_token', Cookies.get('admin_portal_access_token') )
  }, [accessToken, refreshToken]);

  useEffect(() => {
    const initialFindUsers = searchParam.get('findUsers') || '';
    const initialSearchBy = (searchParam.get('searchBy') as 'username' | 'email') || 'username';
  
    if (initialFindUsers) setKeyword(initialFindUsers);
  
    // ✅ Atur pilihan awal berdasarkan URL
    setSearchBy({
      label: initialSearchBy === 'email' ? 'Email' : 'Username',
      value: initialSearchBy,
    });
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const [users, searchedUser, setSearchedUser] = userStore((state) => [
    state.users,
    state.userSearched,
    state.setUserSearched,
  ]);
  const { currentPage, setCurrentPage, setPageSize, totalPages, options } =
    tableStore((state) => state);

  // Sort state — synced with URL
  const sortParam = searchParam.get('sort') || 'created_at:desc';
  const sortOrder = (sortParam.split(':')[1] as 'asc' | 'desc') || 'desc';

  const handleToggleSort = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    const newParams = new URLSearchParams(searchParam);
    newParams.set('sort', `created_at:${newOrder}`);
    newParams.set('page', '1');
    setCurrentPage(1);
    setSearchParam(newParams);
  };

  // Compute start number for page-aware row numbering
  const rawSize = Number(searchParam.get('size'));
  const pageSize = rawSize && rawSize > 0 ? rawSize : 10;
  const startNumber = (currentPage - 1) * pageSize;

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    searchParam.set('page', `${pageNumber}`);
    setSearchParam(searchParam);
  };

  const handleDataSizeOption = (dataSizeOption: number) => {
    const pageParam = searchParam.get('page');
    const page = Number(pageParam);
  
    if (page !== null && !isNaN(page) && page > 1) {
      setCurrentPage(1);
      searchParam.set('page', `${1}`);
      setPageSize(dataSizeOption);
      searchParam.set('size', `${dataSizeOption}`);
      setSearchParam(searchParam);
    } else {
      setPageSize(dataSizeOption);
      searchParam.set('size', `${dataSizeOption}`);
      setSearchParam(searchParam);
    }
  };

  const [keyword, setKeyword] = useState<string>('');
  const { searchTerm } = useDebounce(keyword);
  const [searchBy, setSearchBy] = useState<{ label: string; value: 'username' | 'email' }>({
    label: 'Username',
    value: 'username',
  });
  

  const onKeywordKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    searchByValue: 'username' | 'email'
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = e.target as HTMLInputElement;
      setKeyword(target.value);
  
      const newParams = new URLSearchParams(searchParam);
      newParams.set('findUsers', target.value);
      newParams.set('searchBy', searchByValue);
      newParams.set('page', '1');
      setCurrentPage(1);
      setSearchParam(newParams);
    }
  };

  const onKeywordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setKeyword('');
    }
  };

  useEffect(() => {
    const newParams = new URLSearchParams(searchParam);
    const currentPageParam = parseInt(newParams.get('page') || '1', 10);
    const currentFindUsers = newParams.get('findUsers') || '';
  
    if (currentFindUsers !== searchTerm && currentPageParam > 1) {
      newParams.set('page', '1');
      setCurrentPage(1);
    }
  
    if (searchTerm.trim() !== '') {
      newParams.set('findUsers', searchTerm);
    } else {
      newParams.delete('findUsers');
      setSearchedUser([]); // ⬅️ Tambahkan ini untuk clear hasil pencarian
    }

    console.log('isFetching', isFetching)
  
    setSearchParam(newParams);
  }, [searchTerm]);
  

  return (
    <article>
      <Container>
        <Box as="main">
          {/* Header */} 
          <Box as="header" mb="1rem">
            <h1 className="text-2xl font-bold text-gray-900">User List</h1>
            <p className="mt-2 text-gray-600">Manage and Change Role of User Apilogy</p>
            <PageHeader
              searchTerm={keyword}
              handleSearchChange={onKeywordKeyDown}
              handleSearchClear={onKeywordChange}
              showSearch
              searchBy={searchBy}
              setSearchBy={setSearchBy}
            />
            
          </Box>
          {/* Table */}
          <UsersTable
            users={searchedUser.length > 0 ? searchedUser : users}
            isLoading={isFetching}
            setUser={() => {}}
            startNumber={startNumber}
            sortOrder={sortOrder}
            onToggleSort={handleToggleSort}
          />
          {/* Pagination */}
          <Box mt="1rem">
            <Flex alignX="flex-end">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onClickPage={handlePageChange}
                variant="rounded"
                onClickOption={handleDataSizeOption}
                aria-disabled={false}
                options={
                  ['10', '20', '30', '40', '50']
                }
              />
            </Flex>
          </Box>
        </Box>
      </Container>
    </article>
  );
};

export default UsersPage;
